import { createContext, useContext } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { UserProfile, UserRole } from '@/lib/supabase';
import { useSupabaseAuth } from '@/hooks/useSupabaseAuth';
import { useAuthMethods } from '@/hooks/useAuthMethods';
import { Permission, hasPermission, hasAnyPermission, hasAllPermissions } from '@/utils/permissions';

interface AuthContextType {
  session: Session | null;
  user: User | null;
  profile: UserProfile | null;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<{ success: boolean }>;
  signUp: (email: string, password: string) => Promise<{ success: boolean; requiresEmailVerification?: boolean }>;
  signOut: () => Promise<void>;
  hasRole: (roles: UserRole | UserRole[]) => boolean;
  hasPermission: (permission: Permission) => boolean;
  hasAnyPermission: (permissions: Permission[]) => boolean;
  hasAllPermissions: (permissions: Permission[]) => boolean;
  isConfigured: boolean;
  authError: string | null;
  resetPassword: (email: string) => Promise<{ success: boolean; error?: string }>;
  confirmPasswordReset: (token: string, newPassword: string) => Promise<{ success: boolean; error?: string }>;
  validatePassword: (password: string) => { valid: boolean; errors: string[] };
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const { session, user, profile, isLoading, isConfigured, authError } = useSupabaseAuth();
  const { 
    signIn, 
    signUp, 
    signOut, 
    hasRole, 
    resetPassword, 
    confirmPasswordReset,
    validatePassword 
  } = useAuthMethods(profile);

  // Permission check methods
  const checkPermission = (permission: Permission): boolean => {
    return hasPermission(profile?.role, permission);
  };

  const checkAnyPermission = (permissions: Permission[]): boolean => {
    return hasAnyPermission(profile?.role, permissions);
  };

  const checkAllPermissions = (permissions: Permission[]): boolean => {
    return hasAllPermissions(profile?.role, permissions);
  };

  const value = {
    session,
    user,
    profile,
    isLoading,
    signIn,
    signUp,
    signOut,
    hasRole,
    hasPermission: checkPermission,
    hasAnyPermission: checkAnyPermission,
    hasAllPermissions: checkAllPermissions,
    isConfigured,
    authError,
    resetPassword,
    confirmPasswordReset,
    validatePassword
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
